import { motion, HTMLMotionProps } from 'framer-motion';
import type { ReactNode } from 'react';

type BadgeVariant = 'success' | 'warning' | 'error' | 'info' | 'neutral';
type BadgeSize = 'sm' | 'md' | 'lg';

interface BadgeProps extends HTMLMotionProps<'span'> {
    children: ReactNode;
    variant?: BadgeVariant;
    size?: BadgeSize;
    pulse?: boolean;
    className?: string;
}

export function Badge({ children, variant = 'neutral', size = 'md', pulse = false, className = '', ...props }: BadgeProps) {
    const variantStyles = {
        success: 'bg-emerald-100 text-emerald-700 border-emerald-300 dark:bg-emerald-500/20 dark:text-emerald-400 dark:border-emerald-500/40',
        warning: 'bg-amber-100 text-amber-700 border-amber-300 dark:bg-amber-500/20 dark:text-amber-400 dark:border-amber-500/40',
        error: 'bg-red-100 text-red-700 border-red-300 dark:bg-red-500/20 dark:text-red-400 dark:border-red-500/40',
        info: 'bg-blue-100 text-blue-700 border-blue-300 dark:bg-blue-500/20 dark:text-blue-400 dark:border-blue-500/40',
        neutral: 'bg-slate-100 text-slate-600 border-slate-300 dark:bg-slate-500/20 dark:text-slate-300 dark:border-slate-500/40'
    };

    const sizeStyles = {
        sm: 'text-[10px] px-2 py-0.5',
        md: 'text-xs px-2.5 py-1',
        lg: 'text-sm px-3 py-1.5'
    };

    return (
        <motion.span
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            className={`
                inline-flex items-center gap-1
                rounded-md border font-bold uppercase tracking-wide
                ${variantStyles[variant]}
                ${sizeStyles[size]}
                ${pulse ? 'animate-pulse' : ''}
                ${className}
            `}
            {...props}
        >
            {pulse && <span className="w-1.5 h-1.5 bg-current rounded-full" />}
            {children}
        </motion.span>
    );
}
